function nextPowerOfTwo(n) {
  if (n <= 1) return 2;
  let size = 2;
  while (size < n) size *= 2;
  return size;
}

function cloneRounds(rounds) {
  return rounds.map((round) => ({
    ...round,
    matches: round.matches.map((match) => ({
      ...match,
      scores: { ...(match.scores ?? {}) },
      roundScores: (match.roundScores ?? []).map((r) => ({ round: r.round, scores: { ...r.scores } })),
    })),
  }));
}

/**
 * 경기 점수는 선수 ID 기준으로 보관 → 경기에서 빠진 선수의 점수는 정리
 * scores: 라운드 합계 { [선수 ID]: { head, body, total } }
 * roundScores: 라운드별 [{ round: 1, scores: { [선수 ID]: { head, body, total } } }] (경기마다 라운드 수가 다를 수 있음)
 */
function pruneScores(rounds) {
  rounds.forEach((round) => {
    round.matches.forEach((match) => {
      const ids = [match.player1, match.player2].map(getPlayerId).filter((id) => id != null);
      const keep = (scores) =>
        Object.fromEntries(
          Object.entries(scores ?? {}).filter(([id]) => ids.some((pid) => String(pid) === id))
        );
      match.scores = keep(match.scores);
      match.roundScores = (match.roundScores ?? [])
        .map((r) => ({ round: r.round, scores: keep(r.scores) }))
        .filter((r) => Object.keys(r.scores).length > 0);
    });
  });
}

/** 라운드 점수 → 선수별 합계 { [선수 ID]: { head, body, total } } */
export function sumRoundScores(roundScores) {
  const sums = {};
  (roundScores ?? []).forEach((r) => {
    Object.entries(r.scores ?? {}).forEach(([id, score]) => {
      const sum = sums[id] ?? { head: 0, body: 0, total: 0 };
      sums[id] = {
        head: sum.head + (score.head ?? 0),
        body: sum.body + (score.body ?? 0),
        total: sum.total + (score.total ?? 0),
      };
    });
  });
  return sums;
}

function fillAdvancementSlots(rounds) {
  for (let r = 1; r < rounds.length; r += 1) {
    rounds[r].matches.forEach((match) => {
      match.player1 = null;
      match.player2 = null;
    });
  }

  for (let r = 0; r < rounds.length - 1; r += 1) {
    rounds[r].matches.forEach((match, matchIndex) => {
      if (!match.winner) return;
      const nextMatch = rounds[r + 1].matches[Math.floor(matchIndex / 2)];
      if (matchIndex % 2 === 0) {
        nextMatch.player1 = match.winner;
      } else {
        nextMatch.player2 = match.winner;
      }
    });
  }
}

function invalidateInvalidWinners(rounds) {
  let changed = false;
  for (let r = 1; r < rounds.length; r += 1) {
    for (const match of rounds[r].matches) {
      if (!match.winner) continue;
      const winnerId = getPlayerId(match.winner);
      const stillInMatch =
        (match.player1 && winnerId === getPlayerId(match.player1)) ||
        (match.player2 && winnerId === getPlayerId(match.player2));
      if (!stillInMatch) {
        match.winner = null;
        changed = true;
      }
    }
  }
  return changed;
}

function propagateWinners(bracket) {
  if (isMatchList(bracket)) return normalizeMatchList(bracket);

  const rounds = cloneRounds(bracket.rounds);

  for (let pass = 0; pass <= rounds.length; pass += 1) {
    fillAdvancementSlots(rounds);
    const changed = invalidateInvalidWinners(rounds);
    if (!changed && pass > 0) break;
  }

  pruneScores(rounds);

  const finalMatch = rounds[rounds.length - 1]?.matches[0];
  const champion = finalMatch?.winner ?? null;

  return { ...bracket, rounds, champion };
}

function shuffleArray(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function getGymKey(player) {
  if (!player) return null;
  const gym = (player.gymName || '').trim();
  return gym || `__solo_${getPlayerId(player)}`;
}

function canPlaceInSlot(player, slot, slots, bracketSize) {
  const opponentSlot = bracketSize - 1 - slot;
  const opponent = slots[opponentSlot];
  if (!opponent) return true;
  return getGymKey(player) !== getGymKey(opponent);
}

/** 1라운드: slots[i] vs slots[bracketSize - 1 - i] — 같은 도장은 최대한 피함 */
function assignRandomBracketSlots(participants, bracketSize) {
  const slots = Array(bracketSize).fill(null);
  const shuffled = shuffleArray(participants);

  const getEmptySlots = () =>
    slots.map((value, index) => (value === null ? index : null)).filter((index) => index !== null);

  shuffled.forEach((player) => {
    const empty = getEmptySlots();
    const validSlots = empty.filter((slot) => canPlaceInSlot(player, slot, slots, bracketSize));
    const candidates = validSlots.length > 0 ? validSlots : empty;
    if (candidates.length === 0) return;

    const slot = candidates[Math.floor(Math.random() * candidates.length)];
    slots[slot] = player;
  });

  return slots;
}

export function computeBracketStats(participants) {
  const male = participants.filter((p) => p.gender === 'M').length;
  const female = participants.filter((p) => p.gender === 'F').length;
  const heights = participants
    .map((p) => Number(p.height))
    .filter((h) => !Number.isNaN(h));
  const avgHeight =
    heights.length > 0
      ? (heights.reduce((sum, h) => sum + h, 0) / heights.length).toFixed(1)
      : '-';
  const dojos = new Set(participants.map((p) => p.gymName).filter(Boolean)).size;

  return {
    total: participants.length,
    male,
    female,
    avgHeight,
    dojos,
  };
}

export function generateBracket(participants) {
  if (!participants || participants.length < 2) {
    return null;
  }

  const bracketSize = nextPowerOfTwo(participants.length);
  const slots = assignRandomBracketSlots(participants, bracketSize);

  const round1Matches = [];
  for (let i = 0; i < bracketSize / 2; i += 1) {
    round1Matches.push({
      player1: slots[i],
      player2: slots[bracketSize - 1 - i],
      winner: null,
      scores: {},
      tbd: false,
    });
  }

  const rounds = [{ title: `${bracketSize}강`, roundIndex: 0, matches: round1Matches }];

  let matchCount = bracketSize / 4;
  while (matchCount >= 1) {
    const title = matchCount === 1 ? '결승' : `${matchCount * 2}강`;
    const matches = Array.from({ length: matchCount }, () => ({
      player1: null,
      player2: null,
      winner: null,
      scores: {},
      tbd: false,
    }));
    rounds.push({ title, roundIndex: rounds.length, matches });
    if (matchCount === 1) break;
    matchCount /= 2;
  }

  return propagateWinners({
    bracketSize,
    rounds,
    champion: null,
  });
}

export function selectBracketWinner(bracket, roundIndex, matchIndex, player) {
  if (!bracket || !player) return bracket;

  const match = bracket.rounds[roundIndex]?.matches[matchIndex];
  if (!match) return bracket;

  const playerId = getPlayerId(player);
  const isPlayer1 = match.player1 && getPlayerId(match.player1) === playerId;
  const isPlayer2 = match.player2 && getPlayerId(match.player2) === playerId;
  if (!isPlayer1 && !isPlayer2) return bracket;

  const rounds = cloneRounds(bracket.rounds);
  const target = rounds[roundIndex].matches[matchIndex];
  target.winner = player;

  return propagateWinners({ ...bracket, rounds });
}

// ---------------------------------------------------------------------
// 1:1 경기 (토너먼트가 아닌 경기 여러 번): 라운드 1개짜리 bracket. 승자 진출 / 우승 없음
// ---------------------------------------------------------------------

export const MATCH_LIST_TYPE = 'matches';
/** 1:1 경기의 라운드 이름 (서버 roundLabel 과 같은 값, 화면에서는 translateRoundLabel 로 번역) */
export const MATCH_LIST_ROUND_LABEL = '1:1 경기';
export const MAX_MATCH_LIST_SIZE = 500;

export function isMatchList(bracket) {
  return bracket?.type === MATCH_LIST_TYPE;
}

function createMatch(player1 = null, player2 = null) {
  return { player1, player2, winner: null, scores: {}, roundScores: [], tbd: false };
}

/** 경기 목록 → bracket. bracketSize 는 경기 수 */
export function createMatchList(matches = []) {
  return {
    type: MATCH_LIST_TYPE,
    bracketSize: matches.length,
    rounds: [{ title: MATCH_LIST_ROUND_LABEL, roundIndex: 0, matches }],
    champion: null,
  };
}

export function getMatchListMatches(bracket) {
  return bracket?.rounds?.[0]?.matches ?? [];
}

/** 경기에서 빠진 선수의 승자/점수 정리 */
function normalizeMatchList(bracket) {
  const rounds = cloneRounds(bracket.rounds);
  rounds.forEach((round) => {
    round.matches.forEach((match) => {
      if (!match.winner) return;
      const winnerId = getPlayerId(match.winner);
      if (winnerId !== getPlayerId(match.player1) && winnerId !== getPlayerId(match.player2)) {
        match.winner = null;
      }
    });
  });
  pruneScores(rounds);
  return { ...bracket, rounds, bracketSize: rounds[0]?.matches.length ?? 0, champion: null };
}

function withMatchListMatches(bracket, matches) {
  return normalizeMatchList({ ...bracket, rounds: [{ ...bracket.rounds[0], matches }] });
}

function pairKey(a, b) {
  return [String(getPlayerId(a)), String(getPlayerId(b))].sort().join('|');
}

/**
 * 참가자를 한 번씩 짝짓는다. 이미 경기한 짝과 같은 도장은 되도록 피한다.
 * 홀수면 남은 1명은 경기 수가 적은 선수와 한 번 더 붙인다.
 */
function pairParticipants(participants, existingMatches = []) {
  if (!participants || participants.length < 2) return [];

  const played = new Set();
  const counts = new Map();
  const countOf = (player) => counts.get(String(getPlayerId(player))) ?? 0;
  const addPair = (a, b) => {
    played.add(pairKey(a, b));
    [a, b].forEach((p) => counts.set(String(getPlayerId(p)), countOf(p) + 1));
  };
  existingMatches.forEach((m) => {
    if (m.player1 && m.player2) addPair(m.player1, m.player2);
  });
  const cost = (a, b) => (played.has(pairKey(a, b)) ? 10 : 0) + (getGymKey(a) === getGymKey(b) ? 1 : 0);
  const pickOpponent = (player, candidates) =>
    candidates.reduce(
      (best, candidate, index) => {
        const value = cost(player, candidate) * 1000 + countOf(candidate);
        return value < best.value ? { index, value } : best;
      },
      { index: 0, value: Infinity }
    ).index;

  const pool = shuffleArray(participants);
  const pairs = [];
  while (pool.length >= 2) {
    const player = pool.shift();
    const [opponent] = pool.splice(pickOpponent(player, pool), 1);
    pairs.push(createMatch(player, opponent));
    addPair(player, opponent);
  }
  if (pool.length === 1) {
    const [player] = pool;
    const others = shuffleArray(participants.filter((p) => getPlayerId(p) !== getPlayerId(player)));
    pairs.push(createMatch(player, others[pickOpponent(player, others)]));
  }
  return pairs;
}

/** 1:1 경기 자동 생성: 참가자마다 1경기씩 */
export function generateMatchList(participants) {
  if (!participants || participants.length < 2) return null;
  return createMatchList(pairParticipants(participants));
}

/** 자동 짝을 한 번 더 만들어 뒤에 붙인다 (이미 붙은 짝은 되도록 피함) */
export function addMatchListPairs(bracket, participants) {
  const matches = getMatchListMatches(bracket);
  const pairs = pairParticipants(participants, matches);
  return withMatchListMatches(bracket, [...matches, ...pairs].slice(0, MAX_MATCH_LIST_SIZE));
}

/** 빈 경기 추가 (선수는 편집 화면에서 고른다) */
export function addMatchListMatch(bracket) {
  const matches = getMatchListMatches(bracket);
  if (matches.length >= MAX_MATCH_LIST_SIZE) return bracket;
  return withMatchListMatches(bracket, [...matches, createMatch()]);
}

/** 경기 선수 변경. 바뀐 선수의 점수와 맞지 않게 된 승자는 지운다 */
export function setMatchListPlayer(bracket, matchIndex, isPlayer1, player) {
  const matches = getMatchListMatches(bracket).map((match, index) =>
    index === matchIndex ? { ...match, [isPlayer1 ? 'player1' : 'player2']: player ?? null } : match
  );
  return withMatchListMatches(bracket, matches);
}

export function removeMatchListMatch(bracket, matchIndex) {
  return withMatchListMatches(
    bracket,
    getMatchListMatches(bracket).filter((_, index) => index !== matchIndex)
  );
}

/** 경기 순서 이동 (delta: -1 위로, +1 아래로) */
export function moveMatchListMatch(bracket, matchIndex, delta) {
  const matches = [...getMatchListMatches(bracket)];
  const target = matchIndex + delta;
  if (target < 0 || target >= matches.length) return bracket;
  [matches[matchIndex], matches[target]] = [matches[target], matches[matchIndex]];
  return withMatchListMatches(bracket, matches);
}

/** 경기가 1개 이상이고 모든 경기에 두 선수와 승자가 있으면 true (1:1 경기 결과 확정 조건) */
export function isMatchListDecided(bracket) {
  const matches = getMatchListMatches(bracket);
  return matches.length > 0 && matches.every((m) => m.player1 && m.player2 && m.winner);
}

/**
 * 경기 결과 입력 (점수 + 승자)
 * roundScores: [{ round, scores: { [선수 ID]: { head, body, total } } }] (점수 합계는 자동 계산)
 * 또는 scores: { [선수 ID]: { head, body, total } } (라운드 없이 합계만), winner: 선수 객체 또는 null (미정)
 */
export function setBracketMatchResult(bracket, roundIndex, matchIndex, { roundScores, scores, winner }) {
  const match = bracket?.rounds[roundIndex]?.matches[matchIndex];
  if (!match) return bracket;

  const players = [match.player1, match.player2].filter(Boolean);
  const isInMatch = (player) => players.some((p) => getPlayerId(p) === getPlayerId(player));
  if (winner && !isInMatch(winner)) return bracket;

  const rounds = cloneRounds(bracket.rounds);
  const target = rounds[roundIndex].matches[matchIndex];
  if (roundScores) {
    target.roundScores = roundScores.map((r) => ({ round: r.round, scores: { ...r.scores } }));
    target.scores = sumRoundScores(target.roundScores);
  } else {
    target.roundScores = [];
    target.scores = { ...(scores ?? {}) };
  }
  target.winner = winner ?? null;

  return propagateWinners({ ...bracket, rounds });
}

/** 경기에서 해당 선수의 점수 { head, body, total } (없으면 null) */
/** 한 선수의 라운드별 점수 [{ round, head, body, total }] */
export function getMatchPlayerRoundScores(match, player) {
  const id = getPlayerId(player);
  if (id == null) return [];
  return (match?.roundScores ?? [])
    .filter((r) => r.scores?.[String(id)])
    .map((r) => ({ round: r.round, ...r.scores[String(id)] }));
}

export function getMatchPlayerScore(match, player) {
  const id = getPlayerId(player);
  if (id == null) return null;
  return match?.scores?.[String(id)] ?? null;
}

export function getPlayerId(player) {
  return player?.id ?? null;
}

/** 전체 대진표 기준 연속 경기 번호 (1부터) */
export function getMatchDisplayNumber(bracket, roundIndex, matchIndex) {
  if (!bracket?.rounds) return matchIndex + 1;
  let offset = 0;
  for (let r = 0; r < roundIndex; r += 1) {
    offset += bracket.rounds[r]?.matches?.length ?? 0;
  }
  return offset + matchIndex + 1;
}

/** 라운드별 경기 번호 범위 (안내용) */
export function getRoundMatchNumberRange(bracket, roundIndex) {
  if (!bracket?.rounds?.[roundIndex]) return null;
  const start = getMatchDisplayNumber(bracket, roundIndex, 0);
  const count = bracket.rounds[roundIndex].matches.length;
  const end = start + count - 1;
  return { start, end, count };
}

export function getRound1SlotIndex(bracketSize, matchIndex, isPlayer1) {
  return isPlayer1 ? matchIndex : bracketSize - 1 - matchIndex;
}

function extractRound1Slots(bracket) {
  const { bracketSize } = bracket;
  const r0 = bracket.rounds[0];
  const slots = Array(bracketSize).fill(null);
  for (let i = 0; i < bracketSize / 2; i += 1) {
    slots[i] = r0.matches[i].player1;
    slots[bracketSize - 1 - i] = r0.matches[i].player2;
  }
  return slots;
}

function rebuildBracketFromRound1Slots(bracket, slots) {
  const rounds = cloneRounds(bracket.rounds);
  const { bracketSize } = bracket;
  const r0 = rounds[0];

  for (let i = 0; i < bracketSize / 2; i += 1) {
    r0.matches[i].player1 = slots[i];
    r0.matches[i].player2 = slots[bracketSize - 1 - i];
    r0.matches[i].winner = null;
    r0.matches[i].scores = {};
    r0.matches[i].roundScores = [];
  }

  for (let r = 1; r < rounds.length; r += 1) {
    rounds[r].matches.forEach((match) => {
      match.player1 = null;
      match.player2 = null;
      match.winner = null;
      match.scores = {};
      match.roundScores = [];
    });
  }

  return propagateWinners({ ...bracket, rounds, champion: null });
}

/** 1라운드 슬롯 두 위치의 참가자(또는 부전승) 교환 — 이후 라운드 진행 기록 초기화 */
export function swapBracketSlots(bracket, slotA, slotB) {
  if (!bracket || slotA === slotB) return bracket;
  const slots = extractRound1Slots(bracket);
  [slots[slotA], slots[slotB]] = [slots[slotB], slots[slotA]];
  return rebuildBracketFromRound1Slots(bracket, slots);
}

export function getBracketRound1Players(bracket) {
  if (!bracket?.rounds?.[0]) return [];

  const players = [];
  bracket.rounds[0].matches.forEach((match) => {
    if (match.player1) players.push(match.player1);
    if (match.player2) players.push(match.player2);
  });
  return players;
}

export function bracketHasRound1Players(bracket) {
  return getBracketRound1Players(bracket).length > 0;
}

/** 저장된 1라운드 참가자 구성이 현재 목록과 같을 때만 대진표 재사용 */
export function shouldReuseBracketForParticipants(bracket, participants) {
  if (!bracketHasRound1Players(bracket) || !participants?.length) return false;

  const round1Ids = getBracketRound1Players(bracket)
    .map((player) => String(getPlayerId(player)))
    .sort();
  const participantIds = participants
    .map((player) => String(getPlayerId(player)))
    .filter((id) => id !== 'null' && id !== 'undefined')
    .sort();

  if (round1Ids.length !== participantIds.length) return false;
  return round1Ids.every((id, index) => id === participantIds[index]);
}

/** 저장된 대진표의 참가자 정보를 최신 목록으로 갱신 */
export function refreshBracketFromParticipants(bracket, participants) {
  if (!bracket) return null;

  const resolve = (player) => {
    if (!player?.id) return player;
    return participants.find((p) => String(getPlayerId(p)) === String(getPlayerId(player))) ?? null;
  };

  const rounds = bracket.rounds.map((round) => ({
    ...round,
    matches: round.matches.map((match) => ({
      ...match,
      player1: match.player1 ? resolve(match.player1) : null,
      player2: match.player2 ? resolve(match.player2) : null,
      winner: match.winner ? resolve(match.winner) : null,
    })),
  }));

  const champion = bracket.champion ? resolve(bracket.champion) : null;
  return propagateWinners({ ...bracket, rounds, champion });
}
